import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Clock, FileEdit, ThumbsUp, File, Pencil, Plus } from 'lucide-react';
import { ReportStato } from '../../../api/apiTypes';
import { FaEye } from 'react-icons/fa';
import CreaReportModal from './CreaReportModal';
import { SOURCE_ID_TO_DOC_TYPE } from './sources';
import { routesMap } from '../../../routes/routesMap';
import {
  fetchConductRiskReports,
  createConductRiskReport,
  uploadConductRiskSource,
  startConductRiskReport,
  fetchCRDocumentClassByType,
} from '../../../api/functionApi';
import { useAxiosInterceptor } from '../../../api/wrappers';
import Swal from 'sweetalert2';

interface ReportItem {
  id:         string;
  name:       string;
  anno:       string;
  created_at: string;
  status:     ReportStato;
}

const STATUS_CONFIG: Record<ReportStato, { icon: React.ReactNode; label: string }> = {
  completed: { icon: <CheckCircle2 className="w-4 h-4 text-emerald-500" />, label: 'Completato' },
  approved:  { icon: <ThumbsUp    className="w-4 h-4 text-emerald-500" />, label: 'Approvato'  },
  generated: { icon: <Clock       className="w-4 h-4 text-blue-400"    />, label: 'Generato'   },
  drafted:   { icon: <Pencil      className="w-4 h-4 text-amber-400"   />, label: 'Bozza'      },
  edited:    { icon: <FileEdit    className="w-4 h-4 text-orange-400"  />, label: 'Modificato'  },
  empty:     { icon: <File        className="w-4 h-4 text-gray-400"    />, label: 'Vuoto'       },
};

const StatusBadge = ({ stato }: { stato: ReportStato }) => {
  const cfg = STATUS_CONFIG[stato];
  return (
    <span className="flex items-center gap-1.5">
      {cfg.icon}
      <span className="text-[11px] font-medium text-gray-600">{cfg.label}</span>
    </span>
  );
};

const ContainerConductRiskListaReportPage = () => {
  useAxiosInterceptor();
  const EyeIcon = FaEye as React.ComponentType<React.SVGProps<SVGSVGElement>>;
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const crReports = await fetchConductRiskReports();
        if (cancelled) return;
        const items: ReportItem[] = crReports.map(r => ({
          id:         r.id,
          name:       r.name,
          anno:       r.fiscal_year ?? String(new Date(r.created_at).getFullYear()),
          created_at: r.created_at,
          status:     r.status,
        }));
        setReports(items);
      } catch (err: any) {
        if (cancelled) return;
        console.error('Errore caricamento report CR:', err);
        setError(err?.message ?? 'Errore nel caricamento dei report');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const handleCreaReport = async (data: { titolo: string; anno: number; files: Record<string, File[]> }) => {
    // Loader visibile per tutta la sequenza create -> upload -> start -> navigate.
    // Senza questo l'utente vede solo la modal chiudersi + la lista, senza feedback.
    Swal.fire({
      title: 'Creazione report in corso...',
      html:  'Caricamento fonti e avvio pipeline: attendi qualche secondo.',
      allowOutsideClick: false,
      allowEscapeKey:    false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      // 1) Crea il report (server pesca il template CR più recente).
      const created = await createConductRiskReport({
        name:              data.titolo,
        description:       '',
        fiscal_year:       String(data.anno),
        prior_fiscal_year: String(data.anno - 1),
      });

      const newReport: ReportItem = {
        id:         created.id,
        name:       created.name,
        anno:       created.fiscal_year ?? String(data.anno),
        created_at: created.created_at,
        status:     created.status,
      };

      // 2) Risolve i document_class_id via /document-classes/by-type/{document_type}
      //    e fa upload in parallelo. Cache locale per evitare chiamate duplicate.
      const docTypeCache: Record<string, Promise<string>> = {};
      const resolveDocClassId = (documentType: string): Promise<string> => {
        if (!docTypeCache[documentType]) {
          docTypeCache[documentType] = fetchCRDocumentClassByType(documentType).then(dc => dc.id);
        }
        return docTypeCache[documentType];
      };

      const failed: string[] = [];
      const uploadJobs: Promise<unknown>[] = [];
      Object.entries(data.files).forEach(([sourceId, fileList]) => {
        const docType = SOURCE_ID_TO_DOC_TYPE[sourceId];
        if (!docType) {
          console.warn(`[CR upload] nessun document_type mappato per "${sourceId}", skip.`);
          return;
        }
        fileList.forEach((file) => {
          // Rinforziamo l'hint server-side mappando l'estensione a un MIME
          // canonico quando file.type non e' settato dal browser.
          const ext = file.name.split('.').pop()?.toLowerCase();
          const mimeFallback = ext === 'html' ? 'text/html'
            : ext === 'htm'  ? 'text/html'
            : ext === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : ext === 'xls'  ? 'application/vnd.ms-excel'
            : ext === 'xlsb' ? 'application/vnd.ms-excel.sheet.binary.macroEnabled.12'
            : ext === 'pdf'  ? 'application/pdf'
            : undefined;
          const mimetype = file.type || mimeFallback;

          uploadJobs.push(
            resolveDocClassId(docType)
              .then(docClassId => uploadConductRiskSource(created.id, file, docClassId, mimetype))
              .catch((err) => {
                console.error(`[CR upload] fallito per ${sourceId}/${file.name}:`, err);
                failed.push(`${sourceId}: ${file.name}`);
              }),
          );
        });
      });
      if (uploadJobs.length > 0) await Promise.all(uploadJobs);

      // Se un upload è fallito interrompiamo prima di /start: il report resta
      // in stato parziale e l'utente può ritentare/aprirlo dalla lista.
      if (failed.length > 0) {
        setReports(prev => [newReport, ...prev]);
        Swal.fire({
          icon: 'warning',
          title: 'Report creato, alcuni upload sono falliti',
          text: `Apri il dettaglio per ritentare. File non caricati: ${failed.join(', ')}`,
        });
        return;
      }

      // 3) Avvia il report (innesca le DAG dei 9 driver lato BE).
      await startConductRiskReport(created.id);

      setReports(prev => [newReport, ...prev]);
      Swal.close();
      navigate(routesMap.CONDUCT_RISK_REPORT_DETAIL(newReport.name), { state: { id: newReport.id } });
    } catch (err: any) {
      console.error('Errore creazione report CR:', err);
      Swal.fire({
        icon: 'error',
        title: 'Errore creazione report',
        text: err?.message ?? 'Si è verificato un errore durante la creazione del report.',
      });
    }
  };

  return (
    <div className="p-6 container-conduct-risk-lista-report-page">
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-colors"
          style={{ backgroundColor: '#1B365D' }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#142a4a')}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#1B365D')}
        >
          Crea Report
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-3.5 text-xs font-semibold text-[#1B365D] whitespace-nowrap">Identificativo</th>
                <th className="text-left px-3 py-3.5 text-xs font-semibold text-[#1B365D] whitespace-nowrap">Anno</th>
                <th className="text-left px-3 py-3.5 text-xs font-semibold text-[#1B365D] whitespace-nowrap">Data creazione</th>
                <th className="text-left px-3 py-3.5 text-xs font-semibold text-[#1B365D] whitespace-nowrap">Stato</th>
                <th className="px-3 py-3.5 text-right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center">
                    <div className="inline-block w-8 h-8 border-[3px] border-gray-200 border-t-[#1B365D] rounded-full animate-spin" />
                  </td>
                </tr>
              )}
              {!loading && reports.length === 0 && !error && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-xs text-gray-400">
                    Nessun report Conduct Risk presente.
                  </td>
                </tr>
              )}
              {!loading && reports.map((report) => (
                <tr key={report.id} className="hover:bg-gray-50/50">
                  <td className="px-3 py-4 text-xs font-medium text-gray-800">{report.name}</td>
                  <td className="px-3 py-4 text-xs text-gray-500">{report.anno}</td>
                  <td className="px-3 py-4 text-xs text-gray-500">
                    {new Date(report.created_at).toLocaleDateString('it-IT')}
                  </td>
                  <td className="px-3 py-4">
                    <StatusBadge stato={report.status} />
                  </td>
                  <td className="px-3 py-4 text-right">
                    <button
                      onClick={() => navigate(routesMap.CONDUCT_RISK_REPORT_DETAIL(report.name), { state: { id: report.id } })}
                      className="bg-textPrimary inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-colors"
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#142a4a')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#1B365D')}
                    >
                      Visualizza
                      <EyeIcon className="ml-2" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CreaReportModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleCreaReport}
      />
    </div>
  );
};

export default ContainerConductRiskListaReportPage;
